<template>
  <div class="tracker-wrapper">
    <img
      alt="Democrat Logo"
      class="candidate-avatar"
      :src="`https://s3.amazonaws.com/embeds.ddhq.io/img/${office === 'President' ? 'biden' : 'dem'}-avatar.png`"
      style="background-color: var(--dem-color)" />

    <div class="tracker">
      <div class="tracker-top">
        <div class="tracker-cand">
          <span class="tracker-cand-name">{{
            office === "President" ? biden.name : $t("message.democrats")
          }}</span>

          <span
            class="tracker-cand-votes"
            style="color: var(--dem-color)">
            {{ office === "President" ? biden.ec : democratSeats }}
          </span>

          <svg
            v-if="office === 'President' || controllingParty === 'democratic'"
            class="tracker-winner-icon"
            fill="var(--dem-color)"
            viewBox="0 0 24 24">
            <path
              d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52
                     2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
          </svg>
        </div>

        <div class="tracker-cand">
          <svg
            v-if="controllingParty === 'republican'"
            class="tracker-winner-icon"
            fill="var(--gop-color)"
            viewBox="0 0 24 24">
            <path
              d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52
                     2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
          </svg>

          <span
            class="tracker-cand-votes"
            style="color: var(--gop-color)">
            {{ office === "President" ? trump.ec : republicanSeats }}
          </span>

          <span class="tracker-cand-name">{{
            office === "President" ? trump.name : $t("message.republicans")
          }}</span>
        </div>
      </div>

      <div
        class="tracker-bar"
        :style="`background: ${getGradient};`" />

      <div class="tracker-bottom">
        <p
          v-if="office === 'President'"
          style="font-weight: 400">
          <span class="president-votes">81,268,720 {{ $t("message.votes") }}</span> (51%)
        </p>

        <p v-else>
          <b :style="`color: ${demSeatsChangedColor}`">{{ demSeatsChangedText }}</b>
          {{ $t("message.seats") }}
        </p>

        <div class="needed-to-win">
          <span>{{ neededToWin }} {{ $t("message.forMajority") }}</span>

          <div
            v-if="showNeededToWin"
            class="needed-to-win-question-mark">
            <svg
              class="needed-to-win-svg"
              viewBox="0 0 24 24">
              <path
                d="M11 18h2v-2h-2v2zm1-16C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48
                      10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8
                      8-3.59 8-8 8zm0-14c-2.21 0-4 1.79-4 4h2c0-1.1.9-2 2-2s2 .9 2 2c0
                      2-3 1.75-3 5h2c0-2.25 3-2.5 3-5 0-2.21-1.79-4-4-4z" />
            </svg>

            <div class="needed-to-win-text">
              {{ $t("message.tiedSenate") }}
            </div>
          </div>
        </div>

        <p
          v-if="office === 'President'"
          style="font-weight: 400; text-align: right">
          <span class="president-votes">74,213,893 {{ $t("message.votes") }}</span> (47%)
        </p>

        <p
          v-else
          style="text-align: right">
          <b :style="`color: ${gopSeatsChangedColor}`">{{ gopSeatsChangedText }}</b>
          {{ $t("message.seats") }}
        </p>
      </div>
    </div>

    <img
      alt="Republican Logo"
      class="candidate-avatar"
      :src="`https://s3.amazonaws.com/embeds.ddhq.io/img/${office === 'President' ? 'trump' : 'gop'}-avatar.png`"
      style="background-color: var(--gop-color)" />
  </div>
</template>

<script>
import { mapGetters } from "vuex";

const props = {
  office: {
    default: "Senate",
    required: true,
    type: String,
    validator(value) {
      return ["Senate", "House", "President"].includes(value);
    },
  },
  showNeededToWin: {
    default: false,
    required: true,
    type: Boolean,
  },
};

const computed = {
  ...mapGetters({
    republicanSeats: "congressControl/republicanSeats",
    republicanSeatsChanged: "congressControl/republicanSeatsChanged",
    democratSeats: "congressControl/democratSeats",
    democratSeatsChanged: "congressControl/democratSeatsChanged",
    controllingParty: "congressControl/controllingParty",
    demVotes: "congressControl/demVotes",
    gopVotes: "congressControl/gopVotes",
  }),
  totalSeats() {
    if (this.office === "Senate") return 100;
    if (this.office === "House") return 435;
    return 538;
  },
  getGradient() {
    if (this.office === "President") {
      return `linear-gradient(to right, #5782aa 0%, #5782aa 56.8773%, #db5c56 56.8773%, #db5c56 100%)`;
    }

    const demPercent = (this.democratSeats / this.totalSeats) * 100;
    const gopPercent = (this.republicanSeats / this.totalSeats) * 100;
    const remaining = 100 - demPercent - gopPercent;
    const demColor = "var(--dem-color)";
    const gopColor = "var(--gop-color)";

    return `linear-gradient(to right, ${demColor} 0%, ${demColor} ${demPercent}%, #e0e0e0 ${demPercent}%, #e0e0e0 ${demPercent + remaining}%, ${gopColor} ${demPercent + remaining}%, ${gopColor} 100%)`;
  },
  neededToWin() {
    if (this.office === "President") return 270;
    if (this.office === "House") return 218;
    return 51;
  },
  demSeatsChangedText() {
    return `${this.democratSeatsChanged > -1 ? "+" : ""}${this.democratSeatsChanged}`;
  },
  demSeatsChangedColor() {
    return this.democratSeatsChanged > -1 ? "var(--gre-color)" : "var(--gop-color)";
  },
  gopSeatsChangedText() {
    return `${this.republicanSeatsChanged > -1 ? "+" : ""}${this.republicanSeatsChanged}`;
  },
  gopSeatsChangedColor() {
    return this.republicanSeatsChanged > -1 ? "var(--gre-color)" : "var(--gop-color)";
  },
};

export default {
  props,
  data: () => ({
    biden: {
      name: "Biden",
      ec: 306,
      votes: 81268720,
      voteMargin: 51,
    },
    trump: {
      name: "Trump",
      ec: 232,
      votes: 74213893,
      voteMargin: 47,
    },
  }),
  computed,
};
</script>

<style scoped>
.tracker-wrapper {
  display: flex;
  margin: 0.5em 0;
}

.tracker {
  width: 100%;
  height: 70px;
  position: relative;
}

.tracker-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.candidate-avatar {
  height: 65px;
  min-width: 65px;
  width: auto;
  border-radius: 50%;
  border: 3px solid white;
  z-index: 4;
}

.tracker-cand {
  align-items: center;
  display: flex;
  gap: 0.25em;
}

.tracker-cand-name,
.tracker-cand-votes {
  font-size: 22px;
}

.tracker-cand-name {
  font-weight: 500;
  padding-bottom: 1px;
}

.tracker-cand-votes {
  font-weight: bold;
}

.tracker-bar {
  position: absolute;
  bottom: 25px;
  left: -5px;
  width: calc(100% + 10px);
  height: 14px;
  z-index: 2;
}

.tracker-bar::after {
  background-color: black;
  content: "";
  display: inline-block;
  height: 25px;
  width: 1px;
  left: 50%;
  position: absolute;
  top: -33%;
}

.tracker-bottom {
  position: absolute;
  width: 100%;
  bottom: 4px;
  display: flex;
  justify-content: space-between;
  font-size: 14px;
  font-weight: 300;
}

.tracker-winner-icon {
  font-size: 20px;
  font-weight: bold;
  width: 1em;
}

.needed-to-win {
  align-items: center;
  display: inline-flex;
  font-size: 14px;
  font-weight: 400;
  gap: 0.25em;
  transform: translateY(0.4em);
}

.needed-to-win-question-mark {
  position: relative;
}

.needed-to-win-question-mark:hover .needed-to-win-text {
  opacity: 1;
}

.needed-to-win-svg {
  fill: #9e9e9e;
  width: 1.2em;
}

.needed-to-win-text {
  background-color: var(--neutral-0);
  border-radius: 0.25em;
  color: #fff;
  font-size: 12px;
  left: 0;
  opacity: 0;
  padding: 0.5em;
  pointer-events: none;
  position: absolute;
  text-align: center;
  top: 0;
  transform: translate(-3.4rem, 1.2rem);
  transition: 0.3s opacity;
  width: 8rem;
}

.tracker-bottom-line-break {
  display: none;
}

@media only screen and (max-width: 500px) {
  .candidate-avatar {
    display: none;
  }

  .tracker-cand-name,
  .tracker-cand-votes {
    font-size: 18px;
  }

  .tracker-bar {
    width: 100%;
    left: 0;
    top: 30px;
  }

  .tracker-bottom {
    width: 100%;
    left: 0;
    line-height: 18px;
    font-size: 13px;
  }

  .tracker-bottom-line-break {
    display: inline-block;
  }

  .needed-to-win-line {
    top: 25px;
  }

  .needed-to-win-text {
    top: 52px;
    width: 80px;
    line-height: 14px;
    font-size: 13px;
  }
}

@media screen and (max-width: 620px) {
  .president-votes {
    display: none;
  }
}
</style>
